import { Vector3 } from 'three'
import type { Bus } from './bus'
import { raycastObb } from './geometry'
import { RAY_SPREAD_X } from './tuning'
import type { RoadCollider } from './roadCollider'

/**
 * Port of `BusMovementHandler.CatchBlockerBus` + `RaycastExtensions.RaycastAllForward`.
 *
 * Three rays fire forward from the bus's front — the `RaycastPoint1..3`
 * children recovered from `ConveyorBox (S/M/L).prefab` — every hit is gathered
 * and sorted by distance, and the nearest one that matters decides the click:
 *
 *   - a collider tagged `Conveyor`, which in the scene is `SplineMesh` (the
 *     generated road itself), means the path is clear and the bus can go;
 *   - a bus standing still, or moving but itself blocked, is a blocker: the
 *     clicked bus rolls up to it and stops;
 *   - a bus that is moving and unblocked is skipped, because it will have
 *     cleared out of the way by the time we arrive.
 *
 * The original relies on Unity not reporting a collider the ray starts inside,
 * which is why the prefab's ray origins sit within the bus's own box. We skip
 * self explicitly instead — see `raycastObb`'s `insideCounts`.
 */

export type ScanResult =
  | { kind: 'clear'; distance: number; point: Vector3; entryPercent: number }
  | { kind: 'blocked'; distance: number; point: Vector3; blocker: Bus | null }

/** The original casts an unbounded ray; this is past the far side of any level. */
const MAX_RAY_DISTANCE = 400

/**
 * 📐 How level two ray hits have to be before the **middle** ray is preferred.
 *
 * `HandleClicked` keeps the whole hit — `_movementTarget` is a
 * `ValueTuple<Collider, Vector3, Transform, float>` — and the `Vector3` in it is
 * the winning ray's own hit point, which `<HandleClicked>b__2` then publishes as
 * `BusCrashedEvent.CrashPosition`. So whichever of the three rays wins decides
 * where the sparks are drawn, 1.5 units either side of the bus's axis or on it.
 *
 * Against a bus parked square in front, the three rays are geometrically level
 * and the winner is whatever the arithmetic says. Measured over the 388 bumps
 * the 40 levels can produce, with all three rays hitting on 357 of them:
 *
 *     exact tie                       114
 *     apart by < 1e-9 (float noise)    24
 *     apart by 1e-9 .. 1e-3           208   <- under a millimetre
 *     apart by more than 1e-3          42
 *
 * The 208 are the parked rotations being a few thousandths of a radian off
 * square. A sub-millimetre difference is not a fact about the collision, but it
 * swung the burst the full 3 units from one wing mirror to the other, and it
 * swung it *differently for neighbouring buses*: the sparks landed off-axis on
 * **94 %** of bumps and both sides showed up within the same level on **35 of
 * 40**. Two identical-looking bumps, sparks on opposite corners.
 *
 * ⚠️ **This is a deliberate departure**, and a small one: `Enumerable.OrderBy`
 * is a stable sort, so the original resolves a true tie to whichever ray comes
 * first in `raycastPoints` — and that list's order is ❓ (see `RAY_SPREAD_X`).
 * The original is therefore not "the middle ray"; it is "some fixed ray", and a
 * port cannot tell which. What a port *can* refuse to do is let sub-millimetre
 * noise pick a different one each time, which is the part that reads as a bug.
 *
 * 1 cm is far below anything the layouts express (the tightest gap in the 40
 * levels is `crashStopDistance` = 0.35) and far above the 1e-3 the noise lives
 * at, so nothing that is genuinely a nearer hit is levelled by it.
 *
 * 🔴 The tie-break is fenced to **bus** hits, and to hits on the **same** bus, on
 * purpose. A road hit carries `percent`, which `dispatch` turns into the entry
 * point onto the loop, and letting this reach across a bus/road tie could flip
 * `clear` and `blocked`. Fenced this way it can only move `point` sideways on a
 * bump, so no rule reads a different number — `npm run solve` is untouched by
 * construction, not by luck.
 */
const RAY_TIE_EPSILON = 0.01

export function scanForward(
  bus: Bus,
  others: readonly Bus[],
  road: RoadCollider,
): ScanResult {
  const origins = bus.rayOrigins()
  const dir = bus.forward()

  let best: {
    distance: number
    originIndex: number
    blocker: Bus | null
    percent: number
  } | null = null

  const consider = (
    distance: number,
    originIndex: number,
    blocker: Bus | null,
    percent: number,
  ) => {
    if (best === null) {
      best = { distance, originIndex, blocker, percent }
      return
    }
    // 📐 Two hits on the same bus, level to within a centimetre, are a dead heat
    // however the arithmetic came out: the middle ray takes it, and a hit that is
    // nearer only inside the epsilon does **not** get to take it back. The
    // comparison has to run both ways — resolving only "a later ray is more
    // central" left the case where a later, wider ray was ahead by a fraction of
    // a millimetre, which is a third of every bump in the 40 levels.
    if (
      blocker !== null &&
      blocker === best.blocker &&
      Math.abs(distance - best.distance) <= RAY_TIE_EPSILON
    ) {
      if (Math.abs(RAY_SPREAD_X[originIndex]) < Math.abs(RAY_SPREAD_X[best.originIndex])) {
        best = { distance, originIndex, blocker, percent }
      }
      return
    }
    if (distance < best.distance) {
      best = { distance, originIndex, blocker, percent }
    }
  }

  for (let i = 0; i < origins.length; i++) {
    const o = origins[i]

    for (const other of others) {
      if (other === bus) continue
      // Only buses still sitting in the lot can block. One already dispatching
      // is treated as clearing, matching the original's IsMoving check.
      if (!other.occupiesLot) continue
      const t = raycastObb(o.x, o.z, dir.x, dir.z, other.obb(), MAX_RAY_DISTANCE)
      if (t !== null) consider(t, i, other, -1)
    }

    const road_ = road.raycast(o.x, o.z, dir.x, dir.z, MAX_RAY_DISTANCE)
    if (road_ !== null) consider(road_.distance, i, null, road_.percent)
  }

  if (best === null) {
    return {
      kind: 'blocked',
      distance: MAX_RAY_DISTANCE,
      point: origins[1].clone(),
      blocker: null,
    }
  }

  const hit = best as {
    distance: number
    originIndex: number
    blocker: Bus | null
    percent: number
  }
  const point = origins[hit.originIndex].clone().addScaledVector(dir, hit.distance)

  if (hit.blocker === null && hit.percent >= 0) {
    return { kind: 'clear', distance: hit.distance, point, entryPercent: hit.percent }
  }
  return { kind: 'blocked', distance: hit.distance, point, blocker: hit.blocker }
}
